import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SiteCard from "@/components/feature/SiteCard";
import SearchForm from "@/components/feature/SearchForm";
import { navSites } from "@/mocks/navSites";
import { categories } from "@/mocks/navCategories";
import useFavorites from "@/hooks/useFavorites";

const hotWords = ["视频", "购物", "外卖", "地图", "开源", "学习", "新闻", "基金"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    setKeyword(query);
    document.title = query
      ? `“${query}”的搜索结果 - 51网址导航`
      : "搜索网址 - 51网址导航";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      query
        ? `51网址导航关于“${query}”的搜索结果，收录相关常用网站并按相关度排序，帮助你快速找到目标站点。`
        : "在51网址导航中按关键词搜索常用网站，支持网站名称、域名与标签的模糊匹配。",
    );
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const scored = navSites
      .map((site) => {
        let score = 0;
        if (site.name.toLowerCase().includes(q)) score += 5;
        if (site.host.toLowerCase().includes(q)) score += 4;
        if (site.tags.some((tag) => tag.toLowerCase().includes(q))) score += 3;
        if (site.summary.toLowerCase().includes(q)) score += 2;
        if (site.description.toLowerCase().includes(q)) score += 1;
        return { site, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || b.site.visits - a.site.visits);
    return scored.map((item) => item.site);
  }, [query]);

  const submitInline = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-6 py-8 md:py-10">
            <nav className="text-xs text-foreground-500 flex items-center gap-1 mb-3">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">搜索结果</span>
            </nav>
            <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
              {query ? `“${query}” 的搜索结果` : "搜索网址"}
            </h1>
            <p className="mt-1.5 text-xs md:text-sm text-foreground-500">
              支持按网站名称、域名与标签模糊匹配
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitInline(keyword);
              }}
              className="mt-4 max-w-2xl flex items-center gap-2"
            >
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入关键词重新搜索"
                  className="w-full h-11 pl-9 pr-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-foreground-400">热门：</span>
              {hotWords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => submitInline(word)}
                  className="px-2.5 py-1 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-5 md:gap-6">
            <div className="min-w-0">
              {!query ? (
                <div className="rounded-lg bg-background-50 border border-background-200 p-8 md:p-10">
                  <h2 className="font-heading font-bold text-base text-foreground-950">
                    输入关键词开始搜索
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500 leading-6">
                    例如搜索“视频”可以找到影音娱乐类网站，“购物”会返回电商平台，“开源”则会匹配到开发者常用站点。
                  </p>
                  <div className="mt-4 max-w-xl">
                    <SearchForm variant="inline" onSearch={submitInline} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <p className="text-sm text-foreground-600">
                      共找到 <span className="font-medium text-primary-700">{results.length}</span> 个相关网站
                      <span className="hidden sm:inline text-xs text-foreground-400 ml-2">
                        （关键词：{query}）
                      </span>
                    </p>
                    <p className="text-[11px] text-foreground-400 flex items-center gap-1">
                      <i className="ri-information-line" />
                      暂不支持多维筛选，缩小关键词更精准
                    </p>
                  </div>

                  {results.length === 0 ? (
                    <div className="rounded-lg bg-background-50 border border-background-200 p-8 md:p-10 text-center">
                      <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                        <i className="ri-search-eye-line text-2xl" />
                      </span>
                      <h2 className="mt-4 font-heading font-bold text-base text-foreground-950">
                        未找到相关网站
                      </h2>
                      <p className="mt-2 text-sm text-foreground-500 leading-6">
                        没有匹配“{query}”的网址。可以尝试调整关键词（例如只输入两三个字），或者从分类入口浏览。
                      </p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        <Link
                          to="/"
                          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-xs cursor-pointer whitespace-nowrap"
                        >
                          返回首页
                        </Link>
                        <Link
                          to="/category/life"
                          className="px-4 py-2 rounded-md bg-background-100 border border-background-200 text-xs text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                        >
                          从分类浏览
                        </Link>
                      </div>
                      <div className="mt-5 flex flex-wrap justify-center gap-1.5">
                        {hotWords.slice(0, 5).map((word) => (
                          <button
                            key={word}
                            type="button"
                            onClick={() => submitInline(word)}
                            className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-600 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                          >
                            {word}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                      {results.map((site) => (
                        <SiteCard
                          key={site.id}
                          site={site}
                          isFavorite={isFavorite(site.id)}
                          onToggleFavorite={toggle}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-lg bg-background-50 border border-background-200 p-4">
                <h2 className="text-sm font-medium text-foreground-950 mb-3">按分类浏览</h2>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-background-100 text-xs text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-sm text-primary-500`} />
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="rounded-lg bg-primary-900 p-4 text-background-50">
                <h2 className="text-sm font-medium">搜索小技巧</h2>
                <ul className="mt-2 space-y-1.5 text-xs text-background-100/80 leading-5">
                  <li>· 输入品牌名或域名更准确</li>
                  <li>· 支持标签词，例如“开源”“外卖”</li>
                  <li>· 结果按相关度与访问量排序</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}